import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, ScrollText, LogOut, Menu, Database, Trophy, Sparkles, Users, Tag, Radio, Award, KeyRound, Cloud,
} from "lucide-react";
import { Button } from "./ui/button";
import { adminApi, tokens } from "../lib/api";

const NAV_GROUPS = [
  {
    label: "Operación",
    items: [
      { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/admin/pedidos", label: "Pedidos portal", icon: Package, testid: "nav-orders" },
      { to: "/admin/catalogo", label: "Catálogo & Sync", icon: Database, testid: "nav-catalog" },
      { to: "/admin/logs", label: "Logs Arcos", icon: ScrollText, testid: "nav-logs" },
      { to: "/admin/cron", label: "Cron & Resend", icon: Radio, testid: "nav-cron" },
      { to: "/admin/contrasenas", label: "Contraseñas clientes", icon: KeyRound, testid: "nav-passwords" },
    ],
  },
  {
    label: "Comercial",
    items: [
      { to: "/admin/comercial", label: "Dashboard Comercial", icon: Sparkles, testid: "nav-comercial" },
      { to: "/admin/vendedores", label: "Ranking portal", icon: Trophy, testid: "nav-vendedores" },
      { to: "/admin/vendedores-arcos", label: "Vendedores Arcos", icon: Users, testid: "nav-vendedores-arcos" },
      { to: "/admin/psm", label: "Precios Especiales", icon: Tag, testid: "nav-psm" },
      { to: "/admin/marcas", label: "Marcas comerciales", icon: Award, testid: "nav-marcas" },
    ],
  },
  {
    label: "Integraciones",
    items: [
      { to: "/admin/integracion-tsol", label: "Integración TSOL", icon: Cloud, testid: "nav-tsol" },
    ],
  },
];

export const AdminLayout = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!tokens.getAdmin()) { navigate("/admin/login"); return; }
    let cancelled = false;
    adminApi.get("/admin/me")
      .then((r) => { if (!cancelled) setMe(r.data); })
      .catch((err) => {
        if (cancelled) return;
        tokens.clearAdmin();
        navigate("/admin/login");
        return null;
      });
    return () => { cancelled = true; };
  }, [navigate]);

  const logout = () => { tokens.clearAdmin(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-cm-cream-alt flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? "fixed inset-0 z-40" : "hidden"} lg:relative lg:block lg:w-64 lg:flex-shrink-0`}>
        <div className="absolute inset-0 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
        <div className="relative h-full w-64 bg-cm-ink text-cm-cream flex flex-col">
          <div className="px-6 py-5 border-b border-cm-cream/10">
            <div className="flex items-center gap-2.5">
              <img src="/cm-logo.jpeg" alt="CM" className="h-10 w-10 rounded-full object-cover ring-2 ring-cm-red/40" />
              <div>
                <div className="font-serif text-sm">CM Calidad y Servicio</div>
                <div className="text-[10px] uppercase tracking-widest text-cm-yellow">Admin</div>
              </div>
            </div>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
            {NAV_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="px-3 mb-1.5 text-[10px] uppercase tracking-[2px] text-cm-yellow/70 font-semibold">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.items.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={n.testid}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-cm-red text-white"
                            : "text-cm-cream/70 hover:bg-white/5 hover:text-cm-cream"
                        }`
                      }
                    >
                      <n.icon className="h-4 w-4 flex-shrink-0" /> {n.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          <div className="px-3 py-4 border-t border-cm-cream/10">
            <div className="px-3 py-2 mb-2">
              <p className="text-[10px] uppercase tracking-widest text-cm-cream/50">Sesión</p>
              <p className="text-xs text-cm-cream font-mono truncate" data-testid="admin-email-display">{me?.email}</p>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-cm-cream/70 hover:bg-cm-red hover:text-white transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4" /> Cerrar sesión
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="lg:hidden flex items-center justify-between bg-white border-b border-cm-border px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="open-admin-sidebar">
            <Menu className="h-5 w-5" />
          </Button>
          <span className="font-serif">CM Admin</span>
          <span className="w-9" />
        </div>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
